import React from 'react';
import { GatsbySanityImageFluid, RawPortableText } from '../types';
import Img from 'gatsby-image/withIEPolyfill';
import BlockContent from '@sanity/block-content-to-react';
import serializers from '../serializers';
import clsx from 'clsx';
import HeroForm, { FormField } from './HeroForm';
import { getPortableTextAsString, slugify, uniqBy } from '../utils/utils';

import * as styles from './Hero.module.scss';

interface HeroFormProps {
  _rawTitle: RawPortableText;
  fields: Array<FormField>;
  submitButtonText: string;
  fileToDownloadAfterSubmit?: {
    asset: {
      url: string;
    };
  };
  thankYouScreen: {
    title: string;
    _rawText?: RawPortableText;
  };
}

export interface HeroProps {
  withForm?: boolean;
  form?: HeroFormProps;
  title: string;
  textOverTitle?: string;
  _rawIntroText?: RawPortableText;
  withProtectedText?: boolean;
  images: Array<{
    asset: {
      url: string;
      fluid: GatsbySanityImageFluid;
    };
  }>;

  mobileImage?: {
    asset: {
      url: string;
      fluid: GatsbySanityImageFluid;
    };
  };
}

const Hero = ({
  withForm,
  form,
  title,
  textOverTitle,
  _rawIntroText,
  withProtectedText,
  images,
  mobileImage,
}: HeroProps): React.ReactElement => {
  const formFieldsWithIds = form?.fields.map(formField => ({
    id: slugify(
      formField.fieldType === 'checkbox'
        ? getPortableTextAsString(formField._rawText)
        : formField.title,
    ),
    ...formField,
  }));

  if (form && formFieldsWithIds!.length !== uniqBy(formFieldsWithIds!, 'id').length) {
    throw new Error(
      'Got duplicate ids in formFieldsWithIds: ' +
        JSON.stringify(formFieldsWithIds!.map(formField => formField.id)),
    );
  }

  return (
    <div
      className={clsx(styles.heroContainer, images.length === 6 && styles.mosaic)}
      id="hero-section"
    >
      {!withForm && images.length === 6 && (
        <div className={styles.mosaicHero}>
          <div className={styles.textContainer}>
            <h1 className={styles.title}>{title}</h1>
            <BlockContent
              renderContainerOnSingleChild
              className={styles.text}
              blocks={_rawIntroText}
              serializers={serializers}
            />
            <div className={styles.mosaicTextBar}></div>
          </div>
          <div className={styles.mosaicImagesContainer}>
            {images.map((image, i) => (
              <Img
                key={i}
                className={styles.mosaicImage}
                loading="eager"
                fadeIn={false}
                fluid={image.asset.fluid}
                alt=""
              />
            ))}
            <div className={styles.mosaicBar}></div>
          </div>
          <Img
            className={styles.mobileImage}
            loading="eager"
            fadeIn={false}
            fluid={mobileImage?.asset.fluid || images[0].asset.fluid}
            alt=""
            objectFit="cover"
          />
        </div>
      )}
      {!withForm && images.length === 1 && (
        <div className={styles.fullWidthHero}>
          <Img
            className={clsx(styles.fullWithImage, !mobileImage && styles.noMobileImage)}
            loading="eager"
            fadeIn={false}
            fluid={images[0].asset.fluid}
            alt=""
            objectFit="cover"
          />
          {mobileImage && (
            <Img
              className={styles.mobileImage}
              loading="eager"
              fadeIn={false}
              fluid={mobileImage?.asset.fluid}
              alt=""
              objectFit="cover"
            />
          )}
          <div className={styles.titleContainer}>
            <h1 className={styles.title}>{title}</h1>
          </div>
        </div>
      )}
      {withForm && form?.fields && (
        <div className={styles.formHero}>
          <div
            className={clsx(styles.imageContainer, mobileImage && styles.withMobileImage)}
            style={{ backgroundImage: `url(${images[0].asset.url})` }}
          >
            <img
              src={mobileImage?.asset.url || images[0].asset.url}
              className={styles.mobileImage}
              alt=""
            />
            <div className={styles.contentContainer}>
              <div className={styles.textContainer}>
                {textOverTitle && (
                  <div className={styles.textOverTitleContainer}>
                    <span className={styles.textOverTitle}>{textOverTitle}</span>
                  </div>
                )}
                <div
                  className={clsx(
                    styles.formTitleAndTextContainer,
                    withProtectedText && styles.protectedText,
                  )}
                >
                  {withProtectedText && <div className={styles.element}></div>}
                  <h1 className={styles.formTitle}>{title}</h1>
                  {_rawIntroText && (
                    <BlockContent
                      renderContainerOnSingleChild
                      className={styles.text}
                      blocks={_rawIntroText}
                      serializers={serializers}
                    />
                  )}
                </div>
              </div>
              {withForm && form._rawTitle && formFieldsWithIds && form.submitButtonText && (
                <div className={styles.formContainer}>
                  <HeroForm
                    title={form._rawTitle}
                    buttonText={form.submitButtonText}
                    fieldsConfigs={formFieldsWithIds}
                    thankYouScreen={form.thankYouScreen}
                    fileUrl={form.fileToDownloadAfterSubmit?.asset.url}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
